import { BehaviorSubject } from 'rxjs';

import { Breakpoint, Breakpoints, ScreenSize, SsrOptions } from './breakpoints-config';
import { NgZone } from '@angular/core';

/**
 * Different utils based on the window object
 */
export class BreakpointsHelper {
    private _mediaQueryList: {
        mql: MediaQueryList;
        breakpoint: Breakpoint;
    }[] = [];

    private timeout?: number;

    breakpoint$ = new BehaviorSubject<Breakpoint[]>(this.matchInitialBreakpoints());

    constructor(
        private _isBrowser: boolean,
        private _breakpoints: Breakpoints = [],
        private _ssrOptions: SsrOptions,
        private _ngZone: NgZone
    ) {
        if (!this._ssrOptions) {
            this._ssrOptions = new SsrOptions();
        }

        this.handleBreakpoints();
    }

    /**
     * Calculate the width of the scrollbar
     */
    getScrollbarWidth(): number {
        if (this._isBrowser) {
            const div = document.createElement('div');
            div.style.overflowY = 'scroll';
            div.style.position = 'absolute';
            document.body.appendChild(div);
            const width = div.offsetWidth;
            document.body.removeChild(div);

            return width;
        }

        return 0;
    }

    /**
     * Calculate the viewport size or fallback to default size in case of server-mode
     */
    getSize(): ScreenSize {
        return !this._isBrowser
            ? this._ssrOptions.defaultSize // default/SSR response is a mobile viewport (e.g. iPhone 6)
            : { width: window.innerWidth, height: window.innerHeight };
    }

    protected handleBreakpoints(): void {
        if (this._isBrowser) {
            this._breakpoints.forEach(breakpoint => {
                const mql = window.matchMedia(breakpoint.mediaQuery);
                mql.addEventListener('change', this.breakpointChanged.bind(this));
                this._mediaQueryList.push({ mql, breakpoint });
            });
        }
    }

    protected pushBreakpoints(): void {
        if (this.timeout) {
            window.clearTimeout(this.timeout);
        }

        this._ngZone.runOutsideAngular(() => {
            this.timeout = window.setTimeout(() => {
                this.breakpoint$.next(this._mediaQueryList.filter(x => x.mql.matches).map(x => x.breakpoint));
            }, 0);
        });
    }

    protected matchInitialBreakpoints(): Breakpoint[] {
        if (this._isBrowser) {
            return this._breakpoints.filter(breakpoint => window.matchMedia(breakpoint.mediaQuery).matches);
        }

        return [this._breakpoints[0]];
    }

    protected breakpointChanged(_: MediaQueryListEvent): void {
        this.pushBreakpoints();
    }
}
